import {useId, useRef} from "react";
import {useLocale, useMessages} from "i18n";
import formatBytes from "core/format/byteFormat";
import {formatPercent} from "core/format/percentFormat";
import {fromPickedFiles, type SelectedFile} from "core/transfer/selection";
import type {SessionSummary} from "core/upload/resume/uploadSession";
import type {ResumeProblem} from "../resume/resumeOutcomes";
import {percentOf} from "./progressMath";
import {Actions, CardRoot, Copy, Notice, PrimaryButton, SecondaryButton, Summary, Title} from "./ResumeUploadCard.styles";

interface ResumeProps {
	summary: SessionSummary;
	/** The browser still has the files, or this page does: continuing needs no picking. */
	filesAtHand: boolean;
	problem: ResumeProblem | null;
	/** What the card is doing while its buttons wait, if anything. */
	busy: "continuing" | "discarding" | null;
	onContinue: () => void;
	onPicked: (selection: SelectedFile[]) => void;
	onDiscard: () => void;
}

/**
 * "Continue your upload" (kitecorp/transferify-ui#3204): an unfinished hosted upload this browser kept, with
 * how much of it storage holds. When the browser no longer has the files, the sender picks the same files or
 * folder again; whether they match is `resume/fileMatching`'s rule, decided by the flow, not here.
 */
export function ResumeUploadCard({summary, filesAtHand, problem, busy, onContinue, onPicked, onDiscard}: ResumeProps) {
	const {sender} = useMessages();
	const copy = sender.resume;
	const {locale} = useLocale();
	const titleId = useId();
	const fileInput = useRef<HTMLInputElement>(null);
	const folderInput = useRef<HTMLInputElement>(null);
	const percent = formatPercent(percentOf(summary.uploadedBytes, summary.totalBytes), locale);
	const problemText = problem === null ? null : {
		interrupted: () => copy.interrupted,
		mismatch: () => problem.kind === "mismatch" ? copy.mismatch(problem.firstMissing, problem.missingCount - 1) : "",
		failed: () => copy.failed,
		elsewhere: () => copy.elsewhere,
		discardFailed: () => copy.discardFailed,
	}[problem.kind]();
	const waiting = busy !== null;
	const picked = (event: React.ChangeEvent<HTMLInputElement>) => {
		const selection = fromPickedFiles(event.target.files);
		// Cleared so picking the same folder again fires `change` again; read the files first.
		event.target.value = "";
		if (selection.length > 0) onPicked(selection);
	};

	return (
		<CardRoot aria-labelledby={titleId} aria-busy={waiting} data-testid="resume-upload-card">
			<Title id={titleId}>{copy.title}</Title>
			<Summary>{copy.summary(sender.filesCount(summary.fileCount), formatBytes(summary.totalBytes, locale, 1), percent)}</Summary>
			{!filesAtHand && <Copy>{copy.pickAgain}</Copy>}
			{problemText !== null && <Notice role="alert">{problemText}</Notice>}
			<input ref={fileInput} type="file" hidden multiple onChange={picked} data-testid="resume-file-input"/>
			<input ref={folderInput} type="file" hidden multiple webkitdirectory="" onChange={picked}
				data-testid="resume-folder-input"/>
			<Actions>
				{filesAtHand ? (
					<PrimaryButton type="button" disabled={waiting} onClick={onContinue}>
						{busy === "continuing" ? copy.continuing : copy.continue}
					</PrimaryButton>
				) : (
					<>
						<PrimaryButton type="button" disabled={waiting} onClick={() => fileInput.current?.click()}>
							{copy.pickFiles}
						</PrimaryButton>
						<SecondaryButton type="button" disabled={waiting} onClick={() => folderInput.current?.click()}>
							{copy.pickFolder}
						</SecondaryButton>
					</>
				)}
				<SecondaryButton type="button" disabled={waiting} onClick={onDiscard}>
					{busy === "discarding" ? copy.discarding : copy.discard}
				</SecondaryButton>
			</Actions>
		</CardRoot>
	);
}

interface AbandonedProps {
	/**
	 * `discarded`: the sender discarded it; `gone`: its transfer no longer exists on the api; `expired`: it was
	 * past saving when the sender came back, and was ended unsent.
	 */
	reason: "discarded" | "gone" | "expired";
	onStartNew: () => void;
}

/** An unfinished upload that is over without being sent, said plainly rather than left to vanish (ui#3204). */
export function AbandonedUploadCard({reason, onStartNew}: AbandonedProps) {
	const {sender} = useMessages();
	const copy = sender.abandoned;
	const titleId = useId();
	const words = {
		discarded: {title: copy.discardedTitle, body: copy.discardedBody},
		gone: {title: copy.goneTitle, body: copy.goneBody},
		expired: {title: copy.expiredTitle, body: copy.expiredBody},
	}[reason];
	return (
		<CardRoot aria-labelledby={titleId} data-testid="abandoned-upload-card">
			<Title id={titleId}>{words.title}</Title>
			<Copy role="status">{words.body}</Copy>
			<Actions>
				<PrimaryButton type="button" onClick={onStartNew}>{copy.startNew}</PrimaryButton>
			</Actions>
		</CardRoot>
	);
}
